import React, {useEffect, useRef, useState} from 'react';
import {View, Text, Animated} from 'react-native';
import Svg, {Path} from 'react-native-svg';
import {DynamicPageStyleSheet} from '../../Styles/DynamicPageStyles';

interface TreeDataItem {
  path: string;
  start: number;
  percentX: number;
  percentY: number;
  scale: number;
}

interface TreeProps {
  treeData: TreeDataItem;
  anim: Animated.Value;
  containerWidth: number;
  containerHeight: number;
}

const FADE_DURATION = 0.1;
const TREE_STROKE = '#E2C482';
const TREE_FILL_COLOR = '#909461';
const TREE_DATA = [
  {
    path: 'M55.7313 3.84652C56.5024 2.15801 58.9007 2.15801 59.6718 3.84652L112.547 119.639C113.202 121.074 112.154 122.705 110.577 122.705H68.5223V146.019C68.5223 147.216 67.5527 148.185 66.3563 148.185H49.0468C47.8505 148.185 46.8807 147.216 46.8807 146.019V122.705H4.82703C3.25006 122.705 2.20145 121.074 2.85633 119.639L55.7313 3.84652Z',
    start: 1 / 15,
    percentX: 0,
    percentY: 0.12,
    scale: 1.1,
  },
  {
    path: 'M55.2277 3.36496C55.9987 1.67644 58.3971 1.67644 59.1681 3.36496L112.043 119.158C112.698 120.592 111.65 122.223 110.073 122.223H68.0187V145.538C68.0187 146.734 67.049 147.704 65.8527 147.704H48.5431C47.3468 147.704 46.3771 146.734 46.3771 145.538V122.223H4.32337C2.7464 122.223 1.69778 120.592 2.35267 119.158L55.2277 3.36496Z',
    start: 5 / 15,
    percentX: 0.2,
    percentY: 0.07,
    scale: 1.1,
  },
  {
    path: 'M56.0355 4.01471C56.8066 2.32619 59.2049 2.32619 59.976 4.01471L112.851 119.808C113.506 121.242 112.458 122.873 110.881 122.873H68.8265V146.188C68.8265 147.384 67.8569 148.354 66.6605 148.354H49.351C48.1547 148.353 47.1849 147.384 47.1849 146.188V122.873H5.13123C3.55426 122.873 2.50565 121.242 3.16053 119.808L56.0355 4.01471Z',
    start: 8 / 15,
    percentX: -0.15,
    percentY: 0.02,
    scale: 1.1,
  },
  {
    path: 'M55.4935 3.87745C56.2646 2.18894 58.6629 2.18894 59.434 3.87745L112.309 119.67C112.964 121.105 111.916 122.736 110.339 122.736H68.2846V146.05C68.2846 147.247 67.3149 148.216 66.1185 148.216H48.809C47.6127 148.216 46.643 147.247 46.643 146.05V122.736H4.58924C3.01227 122.736 1.96365 121.105 2.61854 119.67L55.4935 3.87745Z',
    start: 11 / 15,
    percentX: 0,
    percentY: -0.08,
    scale: 1.1,
  },
  {
    path: 'M55.7216 3.48568C56.4926 1.79717 58.891 1.79717 59.662 3.48568L112.537 119.279C113.192 120.713 112.144 122.344 110.567 122.344H68.5126V145.659C68.5126 146.855 67.5429 147.825 66.3466 147.825H49.037C47.8407 147.824 46.871 146.855 46.871 145.659V122.344H4.81727C3.2403 122.344 2.19168 120.713 2.84656 119.279L55.7216 3.48568Z',
    start: 13 / 15,
    percentX: -0.11,
    percentY: -0.21,
    scale: 1.1,
  },
];

const Tree = ({treeData, anim, containerWidth, containerHeight}: TreeProps) => {
  const {start, percentX, percentY, scale, path} = treeData;

  const translateX = percentX * containerWidth;
  const translateY = percentY * containerHeight;

  const fadeInEnd = start + FADE_DURATION;
  const opacity = anim.interpolate({
    inputRange: [start, fadeInEnd],
    outputRange: [0, 1],
    extrapolate: 'clamp',
  });

  if (containerWidth === 0) {
    return null;
  }

  return (
    <Animated.View
      style={[
        DynamicPageStyleSheet.treeWrapper,
        {
          opacity,
          transform: [
            {translateX: translateX},
            {translateY: translateY},
            {scale: scale},
          ],
        },
      ]}>
      <Svg width={115} height={150} viewBox='0 0 115 150'>
        <Path
          d={path}
          fill={TREE_FILL_COLOR}
          stroke={TREE_STROKE}
          strokeWidth='2'
        />
      </Svg>
    </Animated.View>
  );
};

export default function ProgressLoader({syncProgress}: {syncProgress: number}) {
  const anim = useRef(new Animated.Value(0)).current;

  const [layout, setLayout] = useState({width: 0, height: 0});

  useEffect(() => {
    anim.stopAnimation(() => {
      Animated.timing(anim, {
        toValue: syncProgress,
        duration: 150,
        useNativeDriver: false,
      }).start();
    });
  }, [syncProgress, anim]);

  const LAST_TREE_FINISH_TIME = 13 / 15 + FADE_DURATION;
  const barWidth = anim.interpolate({
    inputRange: [0, LAST_TREE_FINISH_TIME, 1],
    outputRange: ['0%', '90%', '102%'],
    extrapolate: 'clamp',
  });

  return (
    <View
      style={DynamicPageStyleSheet.loadBarContainer}
      onLayout={event => {
        const {width, height} = event.nativeEvent.layout;
        setLayout({width, height});
      }}>
      <View style={[DynamicPageStyleSheet.treeContainer, {zIndex: 0}]}>
        {[...TREE_DATA].reverse().map((tree, i) => (
          <Tree
            key={i}
            treeData={tree}
            anim={anim}
            containerWidth={layout.width}
            containerHeight={layout.height}
          />
        ))}
      </View>

      <View
        style={[DynamicPageStyleSheet.progressLoadBarContainer, {zIndex: 1}]}>
        <View style={DynamicPageStyleSheet.loadBarBorder}>
          <View style={DynamicPageStyleSheet.loadBarBackground}>
            <Animated.View
              style={[DynamicPageStyleSheet.loadBarFill, {width: barWidth}]}
            />
          </View>
        </View>
      </View>
      <Text style={{fontSize: 20, color: '#00645E', textAlign: 'center'}}>
        Loading Data...
      </Text>
    </View>
  );
}
